// src/pages/Products.js
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useData } from "../context/DataContext";
import ProductCard from "../components/ProductCard";
import LoadingSpinner from "../components/LoadingSpinner";
import "./Products.css";

export default function Products() {
  const { products, niches, loading } = useData();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState("all");

  const activeNiche = searchParams.get("niche") || "all";

  const setNiche = (val) => {
    const next = new URLSearchParams(searchParams);
    if (!val || val === "all") next.delete("niche");
    else next.set("niche", val);
    setSearchParams(next);
  };

  useEffect(() => { setActiveCategory("all"); }, [activeNiche]);

  const categories = ["all", ...new Set(products.map((p) => p.category).filter(Boolean))];

  const filtered = products.filter((p) => {
    if (p.comingSoon) return false;
    const nicheOk = activeNiche === "all" || p.niches?.includes(activeNiche);
    const catOk = activeCategory === "all" || p.category === activeCategory;
    return nicheOk && catOk;
  });

  const activeNicheData = niches.find((n) => n.id === activeNiche);

  return (
    <div className="products-page">
      <section className="products-hero">
        <div className="container">
          <div className="products-hero__content">
            <span className="badge">🛒 Our Range</span>
            <h1 className="products-hero__title">
              {activeNicheData ? `${activeNicheData.icon} ${activeNicheData.name}` : "All Products"}
            </h1>
            <p className="products-hero__subtitle">
              {activeNicheData
                ? activeNicheData.description
                : "Explore our growing range of premium organic solutions crafted with care for your soil and plants."}
            </p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="products-filters">
            <div className="filter-group">
              <span className="filter-label">Filter by Niche:</span>
              <div className="filter-pills">
                <button className={`filter-pill ${activeNiche === "all" ? "filter-pill--active" : ""}`} onClick={() => setNiche("all")}>
                  🌍 All Niches
                </button>
                {niches.map((n) => (
                  <button key={n.id} className={`filter-pill ${activeNiche === n.id ? "filter-pill--active" : ""}`} onClick={() => setNiche(n.id)}>
                    {n.icon} {n.name}
                  </button>
                ))}
              </div>
            </div>

            {categories.length > 2 && (
              <div className="filter-group">
                <span className="filter-label">Category:</span>
                <div className="filter-pills">
                  {categories.map((cat) => (
                    <button key={cat} className={`filter-pill ${activeCategory === cat ? "filter-pill--active" : ""}`} onClick={() => setActiveCategory(cat)}>
                      {cat === "all" ? "All Categories" : cat}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {loading ? (
            <LoadingSpinner message="Loading products..." />
          ) : filtered.length > 0 ? (
            <>
              <p className="products-count">
                Showing <strong>{filtered.length}</strong> product{filtered.length !== 1 ? "s" : ""}
                {activeNicheData ? ` in ${activeNicheData.name}` : ""}
              </p>
              <div className="products-page__grid">
                {filtered.map((p) => (<ProductCard key={p.id} product={p} />))}
                <ProductCard product={{ comingSoon: true }} />
              </div>
            </>
          ) : (
            <div className="products-empty">
              <span>🔍</span>
              <p>No products match your current filters.</p>
              <button className="btn btn-outline" onClick={() => { setNiche("all"); setActiveCategory("all"); }}>
                Clear All Filters
              </button>
            </div>
          )}

          <div className="coming-soon-section">
            <div className="coming-soon-inner">
              <span className="coming-soon-icon">🌱</span>
              <div>
                <h3>More Products Coming Soon</h3>
                <p>We are continuously working on expanding our product range — new fertilizers, soil enhancers, and organic solutions.</p>
                <a href="mailto:info@sunaharco.com?subject=New Product Notification Request" className="btn btn-outline" style={{ marginTop: 16 }}>
                  Get Notified → info@sunaharco.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
